import { env } from "@/env/client";
import clsx from "clsx";

export type TravelImage = {
  type: "travel";
  id: string;
  name: string;
  realname: string;
  crop?: {
    unit: "%";
    width: number;
    height: number;
    x: number;
    y: number;
  };
};

export type DestinationImage = {
  type: "destination";
  id: string;
  name: string;
  realname: string;
};

export type ThemeImage = {
  type: "theme";
  id: string;
  name: string;
  realname: string;
};

export type SpeakerImage = {
  type: "speaker";
  id: string;
  name: string;
  realname: string;
};

export type GuestSpeakerImage = {
  type: "guest";
  id: string;
  name: string;
  realname: string;
};

export type HotelImage = {
  type: "hotel";
  id: string;
  name: string;
  realname: string;
};

export type BoatImage = {
  type: "boat";
  id: string;
  name: string;
  realname: string;
};

export type AssetImage = {
  type: "asset";
  name: string;
  realname: string;
};

export type FileImage = {
  type: "file";
  uuid: string;
  ext: string;
  name: string;
  crop?: {
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
  };
};

export type ImageUnion =
  | TravelImage
  | SpeakerImage
  | AssetImage
  | FileImage
  | DestinationImage
  | ThemeImage
  | GuestSpeakerImage
  | HotelImage
  | BoatImage;

type Props = {
  height?: { type: "height"; value: number } | { type: "ratio"; value: number };
  sizes: string;
  image: ImageUnion;
  alt: string;
  title?: string;
  objectFit?: "contain" | "cover";
  className?: string;
  isInitVisible?: boolean;
  loading?: "lazy" | "eager";
  imgClassName?: string;
};
export const Image = ({
  height,
  sizes,
  image,
  alt,
  title,
  objectFit = "cover",
  className,
  isInitVisible = false,
  loading = "lazy",
  imgClassName,
}: Props) => {
  const name = encodeURI(image.name);
  const baseUrl = env.assetsUrl;
  const baseTravel = `${baseUrl}images/travels/`;
  const baseDestination = `${baseUrl}images/destinations/`;
  const baseTheme = `${baseUrl}images/themes/`;
  const baseSpeakers = `${baseUrl}images/speakers/`;
  const baseGuests = `${baseUrl}images/guests/`;
  const baseHotels = `${baseUrl}images/hotels/`;
  const baseBoats = `${baseUrl}images/boats-images/`;
  const baseAsset = `${baseUrl}images/asset/`;
  const baseFile = `${baseUrl}images/files/`;
  const query = height ? `?${height.type}=${height.value}` : "";

  const getUrls = () => {
    switch (image.type) {
      case "travel": {
        const realname = encodeURI(image.realname);
        if (image.crop && height) {
          const w = image.crop.width === 100 ? 0 : image.crop.width / 100;
          const h = image.crop.height === 100 ? 0 : image.crop.height / 100;
          const x = image.crop.x / 100;
          const y = image.crop.y / 100;

          return `${baseTravel}${image.id}/${name}/1920/${realname}?${height.type}=${height.value}&cw=${w}&ch=${h}&ox=${x}&oy=${y} 1920w, 
                ${baseTravel}${image.id}/${name}/1440/${realname}?${height.type}=${height.value}&cw=${w}&ch=${h}&ox=${x}&oy=${y} 1440w, 
                ${baseTravel}${image.id}/${name}/1280/${realname}?${height.type}=${height.value}&cw=${w}&ch=${h}&ox=${x}&oy=${y} 1280w, 
                ${baseTravel}${image.id}/${name}/960/${realname}?${height.type}=${height.value}&cw=${w}&ch=${h}&ox=${x}&oy=${y} 960w, 
                ${baseTravel}${image.id}/${name}/640/${realname}?${height.type}=${height.value}&cw=${w}&ch=${h}&ox=${x}&oy=${y} 640w, 
                ${baseTravel}${image.id}/${name}/320/${realname}?${height.type}=${height.value}&cw=${w}&ch=${h}&ox=${x}&oy=${y} 320w`;
        }
        if (image.crop) {
          const w = image.crop.width === 100 ? 0 : image.crop.width / 100;
          const h = image.crop.height === 100 ? 0 : image.crop.height / 100;
          const x = image.crop.x / 100;
          const y = image.crop.y / 100;

          return `${baseTravel}${image.id}/${name}/1920/${realname}?cw=${w}&ch=${h}&ox=${x}&oy=${y} 1920w, 
                ${baseTravel}${image.id}/${name}/1440/${realname}?cw=${w}&ch=${h}&ox=${x}&oy=${y} 1440w, 
                ${baseTravel}${image.id}/${name}/1280/${realname}?cw=${w}&ch=${h}&ox=${x}&oy=${y} 1280w, 
                ${baseTravel}${image.id}/${name}/960/${realname}?cw=${w}&ch=${h}&ox=${x}&oy=${y} 960w, 
                ${baseTravel}${image.id}/${name}/640/${realname}?cw=${w}&ch=${h}&ox=${x}&oy=${y} 640w, 
                ${baseTravel}${image.id}/${name}/320/${realname}?cw=${w}&ch=${h}&ox=${x}&oy=${y} 320w`;
        }
        return `${baseTravel}${image.id}/${name}/1920/${realname}${query} 1920w, 
                ${baseTravel}${image.id}/${name}/1440/${realname}${query} 1440w, 
                ${baseTravel}${image.id}/${name}/1280/${realname}${query} 1280w, 
                ${baseTravel}${image.id}/${name}/960/${realname}${query} 960w, 
                ${baseTravel}${image.id}/${name}/640/${realname}${query} 640w, 
                ${baseTravel}${image.id}/${name}/320/${realname}${query} 320w`;
      }
      case "destination": {
        const realname = encodeURI(image.realname);
        return `${baseDestination}${image.id}/${name}/1920/${realname}${query} 1920w, 
                ${baseDestination}${image.id}/${name}/1440/${realname}${query} 1440w, 
                ${baseDestination}${image.id}/${name}/1280/${realname}${query} 1280w, 
                ${baseDestination}${image.id}/${name}/960/${realname}${query} 960w, 
                ${baseDestination}${image.id}/${name}/640/${realname}${query} 640w, 
                ${baseDestination}${image.id}/${name}/320/${realname}${query} 320w`;
      }
      case "theme": {
        const realname = encodeURI(image.realname);
        return `${baseTheme}${image.id}/${name}/1920/${realname}${query} 1920w, 
                ${baseTheme}${image.id}/${name}/1440/${realname}${query} 1440w, 
                ${baseTheme}${image.id}/${name}/1280/${realname}${query} 1280w, 
                ${baseTheme}${image.id}/${name}/960/${realname}${query} 960w, 
                ${baseTheme}${image.id}/${name}/640/${realname}${query} 640w, 
                ${baseTheme}${image.id}/${name}/320/${realname}${query} 320w`;
      }
      case "speaker": {
        if (name === "") return `${baseAsset}user/64/user.jpg${query}`;
        const realname = encodeURI(image.realname);
        return `${baseSpeakers}${image.id}/${name}/288/${realname}${query} 288w,
                ${baseSpeakers}${image.id}/${name}/192/${realname}${query} 192w, 
                ${baseSpeakers}${image.id}/${name}/64/${realname}${query} 64w`;
      }
      case "guest": {
        const realname = encodeURI(image.realname);
        return `${baseGuests}${image.id}/${name}/182/${realname}${query} 182w, 
                ${baseGuests}${image.id}/${name}/56/${realname}${query} 56w`;
      }
      case "hotel": {
        const realname = encodeURI(image.realname);
        return `${baseHotels}${image.id}/${name}/1280/${realname}${query} 1280w, 
                ${baseHotels}${image.id}/${name}/960/${realname}${query} 960w, 
                ${baseHotels}${image.id}/${name}/640/${realname}${query} 640w, 
                ${baseHotels}${image.id}/${name}/320/${realname}${query} 320w`;
      }
      case "boat": {
        const realname = encodeURI(image.realname);
        return `${baseBoats}${image.id}/${name}/1280/${realname}${query} 1280w, 
                ${baseBoats}${image.id}/${name}/960/${realname}${query} 960w, 
                ${baseBoats}${image.id}/${name}/640/${realname}${query} 640w, 
                ${baseBoats}${image.id}/${name}/320/${realname}${query} 320w`;
      }
      case "asset": {
        const realname = encodeURI(image.realname);
        return `${baseAsset}${name}/1920/${realname}${query} 1920w,
                ${baseAsset}${name}/1440/${realname}${query} 1440w,
                ${baseAsset}${name}/1280/${realname}${query} 1280w,
                ${baseAsset}${name}/960/${realname}${query} 960w,
                ${baseAsset}${name}/440/${realname}${query} 440w, 
                ${baseAsset}${name}/220/${realname}${query} 220w, 
                ${baseAsset}${name}/96/${realname}${query} 96w,
                ${baseAsset}${name}/40/${realname}${query} 40w`;
      }
      case "file": {
        const uuid = encodeURI(image.uuid);
        if (image.crop && height) {
          return `${baseFile}${uuid}${image.ext}/1920/${name}${image.ext}?${height.type}=${height.value}&cw=${image.crop.width}&ch=${image.crop.height}&ox=${image.crop.offsetX}&oy=${image.crop.offsetY} 1920w,
                ${baseFile}${uuid}${image.ext}/1440/${name}${image.ext}?${height.type}=${height.value}&cw=${image.crop.width}&ch=${image.crop.height}&ox=${image.crop.offsetX}&oy=${image.crop.offsetY} 1440w,
                ${baseFile}${uuid}${image.ext}/1280/${name}${image.ext}?${height.type}=${height.value}&cw=${image.crop.width}&ch=${image.crop.height}&ox=${image.crop.offsetX}&oy=${image.crop.offsetY} 1280w,
                ${baseFile}${uuid}${image.ext}/960/${name}${image.ext}?${height.type}=${height.value}&cw=${image.crop.width}&ch=${image.crop.height}&ox=${image.crop.offsetX}&oy=${image.crop.offsetY} 960w,
                ${baseFile}${uuid}${image.ext}/640/${name}${image.ext}?${height.type}=${height.value}&cw=${image.crop.width}&ch=${image.crop.height}&ox=${image.crop.offsetX}&oy=${image.crop.offsetY} 640w,
                ${baseFile}${uuid}${image.ext}/320/${name}${image.ext}?${height.type}=${height.value}&cw=${image.crop.width}&ch=${image.crop.height}&ox=${image.crop.offsetX}&oy=${image.crop.offsetY} 320w`;
        }
        if (image.crop) {
          return `${baseFile}${uuid}${image.ext}/1920/${name}${image.ext}?cw=${image.crop.width}&ch=${image.crop.height}&ox=${image.crop.offsetX}&oy=${image.crop.offsetY} 1920w,
                ${baseFile}${uuid}${image.ext}/1440/${name}${image.ext}?cw=${image.crop.width}&ch=${image.crop.height}&ox=${image.crop.offsetX}&oy=${image.crop.offsetY} 1440w,
                ${baseFile}${uuid}${image.ext}/1280/${name}${image.ext}?cw=${image.crop.width}&ch=${image.crop.height}&ox=${image.crop.offsetX}&oy=${image.crop.offsetY} 1280w,
                ${baseFile}${uuid}${image.ext}/960/${name}${image.ext}?cw=${image.crop.width}&ch=${image.crop.height}&ox=${image.crop.offsetX}&oy=${image.crop.offsetY} 960w,
                ${baseFile}${uuid}${image.ext}/640/${name}${image.ext}?cw=${image.crop.width}&ch=${image.crop.height}&ox=${image.crop.offsetX}&oy=${image.crop.offsetY} 640w,
                ${baseFile}${uuid}${image.ext}/320/${name}${image.ext}?cw=${image.crop.width}&ch=${image.crop.height}&ox=${image.crop.offsetX}&oy=${image.crop.offsetY} 320w`;
        }
        return `${baseFile}${uuid}${image.ext}/1920/${name}${image.ext}${query} 1920w, 
                ${baseFile}${uuid}${image.ext}/1440/${name}${image.ext}${query} 1440w, 
                ${baseFile}${uuid}${image.ext}/1280/${name}${image.ext}${query} 1280w, 
                ${baseFile}${uuid}${image.ext}/960/${name}${image.ext}${query} 960w, 
                ${baseFile}${uuid}${image.ext}/640/${name}${image.ext}${query} 640w, 
                ${baseFile}${uuid}${image.ext}/320/${name}${image.ext}${query} 320w`;
      }
      default:
        return "";
    }
  };

  return (
    <>
      <div
        style={{
          ...(height?.type === "ratio" ? { paddingBottom: `${height.value}%` } : {}),
          ...(height?.type === "height"
            ? {
                height: `${height.value}px`,
              }
            : {}),
        }}
        className={clsx(className, "relative overflow-hidden")}
      >
        <img
          srcSet={getUrls()}
          alt={alt}
          title={title}
          sizes={sizes}
          loading={isInitVisible ? undefined : loading}
          fetchPriority={isInitVisible ? "high" : undefined}
          className={clsx(imgClassName, "absolute top-0 left-0 w-full h-full text-transparent object-center", {
            "object-cover": objectFit === "cover",
            "object-contain": objectFit === "contain",
          })}
        />
      </div>
    </>
  );
};
